import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ExpandMore as ExpandMoreIcon,
  ExitToApp as ExitIcon,
  Dashboard as DashboardIcon,
  Assessment as DeliverablesIcon,
  CheckCircle as TasksIcon,
  CheckCircleOutline as FinalDeliverablesIcon,
  Description as ResourcesIcon,
  AccountBalance as FinancialsIcon
} from '@mui/icons-material';
import { CircularProgress, Box, Typography, Container, Paper } from '@mui/material';
import '../styles/Dashboard.css';
import '../styles/ClientLayout.css';
import UserDropdown from './UserDropdown';

const PAGE_TITLES = {
  dashboard: 'Dashboard',
  deliverables: 'Deliverables',
  todos: 'ToDos',
  'final-deliverables': 'Final Deliverables',
  resources: 'Resources',
  financials: 'Financials'
};

const ClientLayout = () => {
  console.log('ClientLayout rendered');
  const navigate = useNavigate();
  const { contractId } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Memoize stable values to prevent unnecessary re-renders
  const userId = useMemo(() => user?.id, [user?.id]);
  const userRole = useMemo(() => user?.role, [user?.role]);
  
  // Get the current page key from the URL
  const pageKey = location.pathname.split('/').pop();
  const pageTitle = PAGE_TITLES[pageKey] || 'Client Portal';

  // Fetch user's contracts - only when userId actually changes
  useEffect(() => {
    const fetchUserContracts = async () => {
      if (!userId) {
        console.log('No user ID available:', user);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setAccessDenied(false);
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
        const url = `${apiBaseUrl}/api/users/${userId}/contracts`;
        console.log('Fetching contracts from:', url);
        console.log('User role:', userRole);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch contracts: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        console.log('Fetched contracts:', data);
        console.log('Contracts count:', data.length);
        setContracts(data);
        
        // For client users, verify they have access to the current contract
        if (userRole === 'client' && contractId) {
          const hasAccess = data.find(contract => contract.contractId === contractId);
          if (!hasAccess) {
            console.log('Client does not have access to contract:', contractId);
            setAccessDenied(true);
            setLoading(false);
            return;
          }
        }
        
        // Find and set the current contract only if we don't already have it
        if (!selectedContract || selectedContract.contractId !== contractId) {
          const current = data.find(contract => contract.contractId === contractId);
          console.log('Current contract found:', current);
          console.log('Contract data structure:', current ? Object.keys(current) : 'No contract found');
          setSelectedContract(current);
        }
      } catch (error) {
        console.error('Error fetching user contracts:', error);
        setAccessDenied(true);
      } finally {
        setLoading(false);
      }
    };

    fetchUserContracts();
  }, [userId, contractId]); // Include contractId to re-verify access when URL changes
  
  // Update selected contract when contractId changes in URL
  useEffect(() => {
    if (contracts.length > 0 && contractId && !accessDenied) {
      const current = contracts.find(contract => contract.contractId === contractId);
      if (current && (!selectedContract || selectedContract.contractId !== contractId)) {
        console.log('Updating selected contract to:', current);
        setSelectedContract(current);
      }
    }
  }, [contractId, contracts, accessDenied]); // Only run when contractId or contracts change

  // Handle contract selection
  const handleContractSelect = (contract) => {
    setSelectedContract(contract);
    setDropdownOpen(false);
    navigate(`/client-view/${contract.contractId}/dashboard`);
  };

  // Handle exit client view (only for admin/team_member)
  const handleExitClientView = () => {
    navigate('/performance');
  };

  // Check if user is admin or team member (can exit client view)
  const canExitClientView = userRole === 'admin' || userRole === 'team_member';

  // Check if a path is active
  const isActive = (path) => {
    return window.location.pathname.includes(path);
  };

  // Show loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress style={{ color: '#8A42EC' }} />
        <Typography variant="body1" color="textSecondary">
          Loading contract access...
        </Typography>
      </Box>
    );
  }

  // Show access denied message for client users
  if (accessDenied && userRole === 'client') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: '#fafafa'
          }}
        >
          <Typography variant="h5" gutterBottom color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You don't have access to this contract. Please contact your account manager.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You will be redirected to your available contracts shortly.
          </Typography>
        </Paper>
        {/* Auto-redirect after 3 seconds */}
        {setTimeout(() => navigate('/client'), 3000)}
      </Container>
    );
  }

  return (
    <div className="dashboard-container client-view">
      {/* Top White Header Bar (matches MainLayout) */}
      <header className="header">
        <div className="header-left">
          <div className="page-title">{pageTitle}</div>
        </div>
        <div className="header-right">
          <UserDropdown />
        </div>
      </header>
      {/* Sidebar Navigation */}
      <nav className="sidebar client-sidebar">
        {/* Logo - Same as MainLayout */}
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Logo" height="40" />
        </div>
        
        {/* Contract Dropdown */}
        <div className="contract-selector">
          <div 
            className="contract-dropdown-trigger"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <div className="contract-info">
              <div className="contract-number">
                {selectedContract?.external_id || selectedContract?.externalId || 'Select Contract'}
              </div>
              <div className="contract-name">
                {selectedContract?.contract_name || selectedContract?.contractName || ''}
              </div>
            </div>
            <ExpandMoreIcon className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
          </div>
          
          {dropdownOpen && (
            <div className="contract-dropdown-menu">
              {contracts.map((contract) => (
                <div
                  key={contract.contractId}
                  className={`contract-option ${contract.contractId === contractId ? 'selected' : ''}`}
                  onClick={() => handleContractSelect(contract)}
                >
                  <div className="contract-option-number">{contract.external_id || contract.externalId}</div>
                  <div className="contract-option-name">{contract.contract_name || contract.contractName}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <div className="client-navigation">
          <div 
            className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={() => navigate(`/client-view/${contractId}/dashboard`)}
          >
            <DashboardIcon className="nav-item-icon" />
            Dashboard
          </div>
          <div 
            className={`nav-item ${isActive('/deliverables') ? 'active' : ''}`}
            onClick={() => navigate(`/client-view/${contractId}/deliverables`)}
          >
            <DeliverablesIcon className="nav-item-icon" />
            Deliverables
          </div>
          <div 
            className={`nav-item ${isActive('/todos') ? 'active' : ''}`}
            onClick={() => navigate(`/client-view/${contractId}/todos`)}
          >
            <TasksIcon className="nav-item-icon" />
            ToDos
          </div>
          <div 
            className={`nav-item ${isActive('/final-deliverables') ? 'active' : ''}`}
            onClick={() => navigate(`/client-view/${contractId}/final-deliverables`)}
          >
            <FinalDeliverablesIcon className="nav-item-icon" />
            Final Deliverables
          </div>
          <div 
            className={`nav-item ${isActive('/resources') ? 'active' : ''}`}
            onClick={() => navigate(`/client-view/${contractId}/resources`)}
          >
            <ResourcesIcon className="nav-item-icon" />
            Resources
          </div>
          <div 
            className={`nav-item ${isActive('/financials') ? 'active' : ''}`}
            onClick={() => navigate(`/client-view/${contractId}/financials`)}
          >
            <FinancialsIcon className="nav-item-icon" />
            Financials
          </div>
        </div>

        {/* Exit Client View Button - Only show for admin/team_member */}
        {canExitClientView && (
          <div className="exit-section">
            <button onClick={handleExitClientView} className="exit-button">
              <ExitIcon className="exit-icon" />
              Exit Client View
            </button>
          </div>
        )}
      </nav>
      {/* Main Content Area */}
      <div className="main-content client-content">
        <div className="scrollable-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default ClientLayout; 